import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import proService, { ProEvent, ProParticipant } from '../../services/proService';
import PlayerAvatar from '../spiele/PlayerAvatar';
import PlayerLink from '../PlayerLink';
import { t } from '../../i18n';
import DynamicSlider from '../slider/DynamicSlider';

/** Werbefläche unter „Spiele heute“ (Marcel 2026-10-05, vorher an der „Heute“-Karte): Admin-Platzhalter auch ohne Spiele */
export const SCORES_SLIDER_LOCATION = 'home_live_pdc_lead';

const ACCENT = '#3fced1';
const MAX_CARDS = 6;

/**
 * "Spiele heute" – Sport-App-Matchkarten (OneFootball-Stil) der heutigen
 * PDC-Events: Duell-Layout mit Avataren links/rechts und großem Score mittig.
 * Klick auf eine Karte → MatchDetail (/spiel/:id), Footer → /spiele.
 * Ohne Spiele: ausgeblendet.
 */
const ProScoresBlock: React.FC<{ events: ProEvent[] }> = ({ events }) => {
  const liveEvents = events.filter((e) => proService.isLive(e));
  const todayEvents = proService.filterAndSortByDay(events, 0);
  const all = [...liveEvents, ...todayEvents];
  const list = all.slice(0, MAX_CARDS);

  if (list.length === 0) {
    // Keine Spiele: nur die Fläche (rendert für Nutzer nichts, wenn leer; Admins sehen den Platzhalter)
    return <DynamicSlider location={SCORES_SLIDER_LOCATION} className="w-full lg:max-w-[400px]" />;
  }

  return (
    <section aria-label={t('web.component.pro-scores-block.spiele-heute')}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{t('web.component.pro-scores-block.spiele-heute')}</h2>
        <Link
          to="/spiele"
          className="flex items-center gap-1 text-xs font-semibold transition-colors hover:text-white"
          style={{ color: ACCENT }}
        >
          {t('web.component.matches-section.alle-spiele')}{' '}<ChevronRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {list.map((event, idx) => (
          <MatchCard key={proService.getEventId(event) || idx} event={event} />
        ))}
      </div>
      {/* Werbefläche direkt unter den Spielen (Marcel 2026-10-05) */}
      <DynamicSlider location={SCORES_SLIDER_LOCATION} className="mt-4 w-full lg:max-w-[400px]" />
    </section>
  );
};

const MatchCard: React.FC<{ event: ProEvent }> = ({ event }) => {
  const id = proService.getEventId(event);
  const isLive = proService.isLive(event);
  const finished = proService.isFinished(event);
  const scores = proService.formatScore(event);
  // Abgesagt / Walkover ohne Sieger: Label statt 0:0 (2026-09-17)
  const resultLabel = proService.getResultLabel(event);
  const voidResult = proService.isVoidResult(event);
  const showScore = (isLive || finished) && !voidResult;
  const tournamentName = event.tournament?.name || 'PDC';
  const time = proService.formatEventTime(event.startTime);
  const timeSuffix = time !== '-' ? ` · ${time}` : '';

  const homeRemaining = event.liveData?.homeRemainingScore;
  const awayRemaining = event.liveData?.awayRemainingScore;
  const showLeg =
    isLive && typeof homeRemaining === 'number' && typeof awayRemaining === 'number';

  const content = (
    <>
      {/* Kopfzeile: Turnier + Status */}
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-[11px] font-medium text-gray-500">
          {tournamentName}
        </span>
        {isLive ? (
          <span className="flex shrink-0 items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-500">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
              {t('web.component.matches-section.live')}
            </span>
            {timeSuffix && (
              <span className="text-[11px] font-medium tabular-nums text-gray-500">
                {time}
              </span>
            )}
          </span>
        ) : resultLabel ? (
          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-amber-500">
            {resultLabel}{timeSuffix}
          </span>
        ) : finished ? (
          <span className="shrink-0 text-[11px] font-medium tabular-nums text-gray-500">
            {t('web.component.live-ticker-bar.beendet', { timeSuffix })}
          </span>
        ) : (
          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-gray-400">
            {t('web.component.pro-scores-block.uhr', { startTime: proService.formatEventTime(event.startTime) })}
          </span>
        )}
      </div>

      {/* Duell: Spieler links/rechts, Score groß mittig */}
      <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <DuelSide participant={event.homeParticipant} won={event.winner === 1} />

        <div className="px-1 text-center">
          {showScore ? (
            <div className="flex items-baseline justify-center gap-1 text-2xl font-extrabold tabular-nums leading-none tracking-tight">
              <span
                className={event.winner === 2 ? 'text-gray-500' : ''}
                style={event.winner === 1 ? { color: ACCENT } : undefined}
              >
                {scores.home}
              </span>
              <span className="text-base font-bold text-gray-600">:</span>
              <span
                className={event.winner === 1 ? 'text-gray-500' : ''}
                style={event.winner === 2 ? { color: ACCENT } : undefined}
              >
                {scores.away}
              </span>
            </div>
          ) : (
            <span className="text-sm font-bold uppercase text-gray-600">vs</span>
          )}
          {showLeg && (
            <div className="mt-1 text-[10px] font-medium tabular-nums text-gray-500">
              {t('web.component.pro-scores-block.leg', { homeRemaining, awayRemaining })}
            </div>
          )}
        </div>

        <DuelSide participant={event.awayParticipant} won={event.winner === 2} />
      </div>
    </>
  );

  const cardClass =
    t('web.component.pro-scores-block.card-lift-block-rounded-2xl');

  if (!id) return <div className={cardClass}>{content}</div>;
  return (
    <Link to={`/spiel/${id}`} className={cardClass}>
      {content}
    </Link>
  );
};

const DuelSide: React.FC<{ participant?: ProParticipant; won: boolean }> = ({
  participant,
  won,
}) => (
  <div className="flex min-w-0 flex-col items-center gap-1.5 text-center">
    <PlayerAvatar participant={participant} size="md" />
    <PlayerLink
      name={proService.getParticipantName(participant)}
      className={`w-full truncate text-xs leading-tight ${
        won ? 'font-bold text-white' : 'font-medium text-gray-300'
      }`}
    />
  </div>
);

export default ProScoresBlock;
